import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCopy, faUsers, faLink } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { primaryColor } from '../../../../../utils/comon';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import { jwtDecode } from "jwt-decode";
import storageService from '../../../../../utils/storageService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import CustomSelect from '../../../../common/formfields/CustomSelect';

const GroupInviteForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Read from location.state if navigating from an existing event
    const [step, setStep] = useState(location.state?.step || 1);
    const [inviteData, setInviteData] = useState(location.state?.inviteData || null);

    const [sites, setSites] = useState([]);
    const [units, setUnits] = useState([]);
    const [loggedInUserRole, setLoggedInUserRole] = useState("");

    const decodedTokenData = useMemo(() => {
        const rawToken = storageService.getItem('Token');
        if (!rawToken) return null;
        try {
            return jwtDecode(rawToken);
        } catch (e) {
            console.error("Failed to decode token", e);
            return null;
        }
    }, []);

    const [formData, setFormData] = useState({
        siteId: null,
        unitId: null,
        userId: decodedTokenData?.id || null,
        eventName: '',
        expectedEntryTime: '',
        expectedExitTime: '',
        maxGuests: '',
        isPrivate: false,
        createdBy: ''
    });

    // Step 2 states

    const [guestName, setGuestName] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [vehicleNumber, setVehicleNumber] = useState("");
    const [vehicleType, setVehicleType] = useState("None");
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registeredGuests, setRegisteredGuests] = useState([]);

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const tokenUserId = decodedTokenData?.id;
                if (tokenUserId) {
                    const userRes = await apiServices({
                        method: 'GET',
                        url: `/Residential/ResUserDetails/getUserDetails?Id=${tokenUserId}&PageNumber=1&PageSize=10`
                    });

                    const isSuccess = userRes?.success === true || userRes?.data?.success === true;
                    if (isSuccess) {
                        const resData = userRes?.data?.Resuserdetailsdata || userRes?.data?.data || userRes?.data || [];
                        const userData = Array.isArray(resData) ? resData[0] : resData;

                        if (userData) {
                            const userSiteId = userData.siteId;
                            const userSiteName = userData.SiteName;
                            const userUnitId = userData.UnitId;
                            const userUnitName = userData.unitName;
                            const roleName = userData.RoleName || userData.roleName || "";

                            setLoggedInUserRole(roleName);

                            if (userSiteId) {
                                setSites([{ label: userSiteName, value: userSiteId }]);
                            }

                            const isSiteAdmin = roleName.toLowerCase().includes('site admin') || roleName.toLowerCase().includes('super');

                            if (isSiteAdmin) {
                                try {
                                    const unitRes = await apiServices({ method: "GET", url: "/UnitMaster/GetAllUnitMaster" });
                                    if (unitRes?.success || unitRes?.IsSuccess) {
                                        const data = unitRes.data?.data || unitRes.data || [];
                                        const formatted = data.map(u => ({
                                            label: `${u.UnitName} (${u.UnitNumber})`,
                                            value: u.UnitId,
                                            siteId: u.SiteId || userSiteId
                                        }));
                                        setUnits(formatted);
                                    }
                                } catch (error) {
                                    console.error("Error fetching units:", error);
                                }
                            } else if (userUnitId) {
                                setUnits([{ label: userUnitName, value: userUnitId, siteId: userSiteId }]);
                            }

                            setFormData(prev => ({
                                ...prev,
                                createdBy: `${userData.FirstName || ''} ${userData.LastName || ''}`.trim() || prev.createdBy,
                                userId: userData.Id || prev.userId,
                                siteId: userSiteId || prev.siteId,
                                unitId: isSiteAdmin ? null : (userUnitId || prev.unitId)
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching master data:", error);
            }
        };
        fetchMasterData();
    }, [decodedTokenData]);

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : null }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleInputChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.siteId) newErrors.siteId = 'Site is required';
        if (!formData.unitId) newErrors.unitId = 'Unit is required';
        if (!formData.eventName.trim()) newErrors.eventName = 'Event Name is required';
        if (!formData.expectedEntryTime) newErrors.expectedEntryTime = 'Expected Entry Time is required';
        if (!formData.expectedExitTime) newErrors.expectedExitTime = 'Expected Exit Time is required';
        if (!formData.maxGuests) newErrors.maxGuests = 'Max Guests is required';

        if (formData.expectedEntryTime && formData.expectedExitTime && new Date(formData.expectedEntryTime) >= new Date(formData.expectedExitTime)) {
            newErrors.expectedExitTime = 'Exit time must be after entry time';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                siteId: formData.siteId,
                unitId: formData.unitId,
                userId: formData.userId,
                inviteType: "Group Invite",
                eventName: formData.eventName,
                expectedEntryTime: new Date(formData.expectedEntryTime).toISOString(),
                expectedExitTime: new Date(formData.expectedExitTime).toISOString(),
                maxGuests: parseInt(formData.maxGuests),
                isPrivate: formData.isPrivate,
                createdBy: formData.createdBy,
                validFromDate: new Date(formData.expectedEntryTime).toISOString(),
                validToDate: new Date(formData.expectedExitTime).toISOString(),
                allowedDaysOfWeek: "All",
                linkExpiryTime: new Date(formData.expectedExitTime).toISOString()
            };

            const response = await apiServices({
                method: "POST",
                url: `/Residential/ResInviteVisitor/v2/Create`,
                data: payload,
            });

            if (response?.success || response?.isSuccess || response?.IsSuccess) {
                toast.success("Group Invitation created successfully.");
                const respData = response?.data?.Data || response?.data?.data || response?.Data || response?.data || response;
                setInviteData(respData);
                setStep(2);
            } else {
                toast.error(response?.message || "Failed to create group invitation");
            }
        } catch (error) {
            console.error("Group invite error", error);
            toast.error("An error occurred while creating group invite");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegisterGuest = async (e) => {
        e.preventDefault();
        if (!guestName || !mobileNumber) {
            toast.error("Please enter guest name and mobile number.");
            return;
        }

        try {
            setRegisterLoading(true);
            const payload = {
                inviteToken: inviteData?.inviteToken || inviteData?.InviteToken,
                guestName: guestName,
                mobileNumber: mobileNumber,
                vehicleNumber: vehicleNumber,
                vehicleType: vehicleType
            };

            const response = await apiServices({
                method: "POST",
                url: `Residential/ResInviteVisitor/v2/Link/Register`,
                data: payload,
            });

            if (response?.success || response?.isSuccess || response?.IsSuccess) {
                const guestData = response?.data?.Data || response?.data?.data || response?.Data || response?.data || response;

                // Automatically validate/send the pass for the registered guest
                if (guestData && guestData.QrNumber1) {
                    try {
                        await apiServices({
                            method: "POST",
                            url: `/Residential/ResInviteVisitor/v2/ValidatePass`,
                            data: {
                                qrNumber: parseInt(guestData.QrNumber1),
                                siteId: parseInt(guestData.SiteId || formData.siteId)
                            }
                        });
                        toast.success("Guest registered & pass sent successfully!");
                    } catch (passError) {
                        console.error("Pass validation error", passError);
                        toast.success("Guest registered, but failed to send pass automatically.");
                    }
                } else {
                    toast.success("Guest registered successfully!");
                }

                setRegisteredGuests(prev => [...prev, {
                    name: guestName,
                    mobile: mobileNumber,
                    vehicle: vehicleType !== "None" ? `${vehicleType} (${vehicleNumber})` : "None"
                }]);
                setGuestName("");
                setMobileNumber("");
                setVehicleNumber("");
                setVehicleType("None");
            } else {
                toast.error(response?.message || "Failed to register guest");
            }
        } catch (error) {
            console.error("Guest register error", error);
            toast.error("An error occurred while registering guest");
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleCopyLink = () => {
        const url = inviteData?.shareableUrl || inviteData?.ShareableUrl;
        if (url) {
            navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard!");
        }
    };

    const localISOTime = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    const filteredUnits = units.filter(u => !formData.siteId || String(u.siteId) === String(formData.siteId));

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={step === 1 ? "Create Group Invite" : "Share & Register Guests"}
                subHeading={step === 1 ? "Invite multiple visitors by creating an event link" : "Share the generated link or register guests manually"}
            />

            {step === 1 ? (
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col flex-1">
                    <div className="mt-4 bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor flex flex-col gap-6 shadow-sm mb-4">

                        {/* Site Information */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Site Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <CustomSelect
                                        label={<>Site Name <span className="text-red-500">*</span></>}
                                        options={sites}
                                        value={sites.find(s => String(s.value) === String(formData.siteId)) || null}
                                        onChange={(opt) => handleSelectChange('siteId', opt)}
                                        placeholder="Select Site"
                                    />
                                    {errors.siteId && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.siteId}</p>}
                                </div>

                                <div>
                                    <CustomSelect
                                        label={<>Unit Name <span className="text-red-500">*</span></>}
                                        options={filteredUnits}
                                        value={filteredUnits.find(u => String(u.value) === String(formData.unitId)) || null}
                                        onChange={(opt) => handleSelectChange('unitId', opt)}
                                        placeholder="Select Unit"
                                        isDisabled={!(loggedInUserRole?.toLowerCase().includes("site admin") || loggedInUserRole?.toLowerCase().includes("super")) || !formData.siteId}
                                    />
                                    {errors.unitId && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.unitId}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Event Information */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-3 pb-2 border-b dark:border-bordercolor">Event Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="eventName">
                                        Event Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.eventName ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                        type="text"
                                        id="eventName"
                                        value={formData.eventName}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Birthday Party, Get Together"
                                    />
                                    {errors.eventName && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.eventName}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="expectedEntryTime">
                                        Expected Entry Time <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.expectedEntryTime ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                        type="datetime-local"
                                        id="expectedEntryTime"
                                        value={formData.expectedEntryTime}
                                        onChange={handleInputChange}
                                        min={localISOTime}
                                    />
                                    {errors.expectedEntryTime && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.expectedEntryTime}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="expectedExitTime">
                                        Expected Exit Time <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full px-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.expectedExitTime ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                        type="datetime-local"
                                        id="expectedExitTime"
                                        value={formData.expectedExitTime}
                                        onChange={handleInputChange}
                                        min={formData.expectedEntryTime || localISOTime}
                                    />
                                    {errors.expectedExitTime && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.expectedExitTime}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="maxGuests">
                                        Max Guests <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative flex items-center">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                            <FontAwesomeIcon icon={faUsers} className="text-xs" />
                                        </div>
                                        <input
                                            className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm outline-none bg-transparent text-textprimary transition-colors ${errors.maxGuests ? 'border-red-500 focus:border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:border-indigo-500'}`}
                                            type="number"
                                            id="maxGuests"
                                            value={formData.maxGuests}
                                            onChange={handleInputChange}
                                            placeholder="Number of guests"
                                            min="1"
                                        />
                                    </div>
                                    {errors.maxGuests && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.maxGuests}</p>}
                                </div>

                                <div className="flex flex-col justify-center mt-6">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                id="isPrivate"
                                                checked={formData.isPrivate}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`block w-10 h-6 rounded-full transition-colors ${formData.isPrivate ? 'bg-[#22C55E]' : 'bg-slate-300'}`}></div>
                                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.isPrivate ? 'translate-x-4' : ''}`}></div>
                                        </div>
                                        <span className="text-xs font-bold text-slate-700 dark:text-textprimary">Private Event</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="flex justify-end gap-3 mt-auto py-4">
                        <button
                            type="button"
                            onClick={() => navigate('/zentres/visitor-managment/invited-visitor')}
                            className="px-6 py-2 border border-gray-200 dark:border-bordercolor rounded-lg text-sm font-bold text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors shadow-sm bg-white dark:bg-cardbg"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{ backgroundColor: primaryColor() }}
                            className="px-6 py-2 rounded-lg text-white text-sm font-bold shadow-sm transition-colors min-w-[140px] flex justify-center items-center"
                        >
                            {isLoading ? (
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            ) : "Create Group Invite"}
                        </button>
                    </div>
                </form>
            ) : (
                <div className="mt-4 bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor flex flex-col gap-8 shadow-sm mb-4">
                    {/* Share Link Section */}
                    <div className="border border-green-200 rounded-2xl bg-green-50/50 p-6 text-center max-w-2xl mx-auto w-full">
                        <h3 className="font-bold text-green-700 text-lg mb-2">Event Created Successfully!</h3>
                        <p className="text-sm text-green-600 font-medium mb-4">Share this link with your guests so they can register themselves.</p>

                        <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-green-200 shadow-sm">
                            <div className="flex-1 truncate text-sm text-slate-600 font-medium px-2 text-left select-all">
                                {inviteData?.shareableUrl || inviteData?.ShareableUrl || "Link could not be loaded"}
                            </div>
                            <button
                                onClick={handleCopyLink}
                                className="bg-white border border-green-600 text-green-700 hover:bg-green-50 px-4 py-2 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shrink-0"
                            >
                                <FontAwesomeIcon icon={faCopy} /> Copy
                            </button>
                        </div>
                    </div>

                    <div className="relative max-w-2xl mx-auto w-full">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                            <div className="w-full border-t border-slate-200 dark:border-bordercolor"></div>
                        </div>
                        <div className="relative flex justify-center">
                            <span className="px-3 bg-sectionheaderbg text-xs font-bold text-slate-400">OR REGISTER MANUALLY</span>
                        </div>
                    </div>

                    {/* Manual Registration Form */}
                    <div className="max-w-2xl mx-auto w-full">
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-4">Register Guest Manually</h3>
                        <form onSubmit={handleRegisterGuest} className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                        Guest Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={guestName}
                                        onChange={(e) => setGuestName(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary hover:border-gray-400 focus:border-indigo-500 transition-colors"
                                        placeholder="Guest name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        value={mobileNumber}
                                        onChange={(e) => setMobileNumber(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary hover:border-gray-400 focus:border-indigo-500 transition-colors"
                                        placeholder="Mobile number"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                        Vehicle Type
                                    </label>
                                    <select
                                        value={vehicleType}
                                        onChange={(e) => setVehicleType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary hover:border-gray-400 focus:border-indigo-500 transition-colors"
                                    >
                                        <option value="None">None</option>
                                        <option value="bike">Bike</option>
                                        <option value="car">Car</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                        Vehicle Number
                                    </label>
                                    <input
                                        type="text"
                                        value={vehicleNumber}
                                        onChange={(e) => setVehicleNumber(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary hover:border-gray-400 focus:border-indigo-500 transition-colors disabled:opacity-50 disabled:bg-gray-50"
                                        placeholder="e.g. GJ-01-AB-1234"
                                        disabled={vehicleType === "None"}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end pt-2">
                                <button
                                    type="submit"
                                    disabled={registerLoading}
                                    style={{ backgroundColor: primaryColor() }}
                                    className="px-6 py-2 rounded-lg text-white text-sm font-bold shadow-sm transition-colors min-w-[140px] flex justify-center items-center"
                                >
                                    {registerLoading ? (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    ) : "Register Guest"}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Registered Guests List */}
                    {registeredGuests.length > 0 && (
                        <div className="max-w-2xl mx-auto w-full mt-6">
                            <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary mb-4 pb-2 border-b border-gray-100 dark:border-bordercolor">
                                Recently Registered Guests ({registeredGuests.length})
                            </h3>
                            <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-gray-50 dark:bg-actionitembg text-gray-500 dark:text-textsecondary font-bold border-b border-gray-100 dark:border-bordercolor">
                                        <tr>
                                            <th className="px-4 py-3">Guest Name</th>
                                            <th className="px-4 py-3">Mobile Number</th>
                                            <th className="px-4 py-3">Vehicle</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-bordercolor">
                                        {registeredGuests.map((guest, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-actionitembg/50 transition-colors">
                                                <td className="px-4 py-2.5 font-bold text-gray-800 dark:text-textprimary">{guest.name}</td>
                                                <td className="px-4 py-2.5 font-medium text-gray-600 dark:text-textsecondary">{guest.mobile}</td>
                                                <td className="px-4 py-2.5 font-medium text-gray-600 dark:text-textsecondary capitalize">{guest.vehicle}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100 dark:border-bordercolor">
                        <button
                            type="button"
                            onClick={() => navigate('/zentres/visitor-managment/invited-visitor')}
                            style={{ backgroundColor: primaryColor() }}
                            className="px-8 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:opacity-90 transition-all flex items-center gap-2 active:scale-95"
                        >
                            Complete & Go to List
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GroupInviteForm;
